import { Suspense, useCallback, useState } from 'react';
import { Burger, AppShell, Group, ScrollArea, Box } from '@mantine/core';
import { Outlet, useLocation } from 'react-router-dom';
import { useClickOutside, useWindowScroll } from '@mantine/hooks';

import Loading from '../content/loading/Loading';
import TopBar from './nav/topbar/Topbar';
import Expand from './nav/navbar/Expand';
import InitialModals from './modals/InitialModals';
import HeaderDisclaimer from './nav/topbar/HeaderDisclaimer';
import Documentation from './nav/navbar/Documentation';
import NewsLink from './nav/navbar/News';
import SidebarThemeToggle from './nav/navbar/SidebarThemeToggle';

import { mapRoutesToMenu } from './nav/navbar/mapRoutesToMenu';
import { SidebarSubmenuAccordionProvider } from './nav/navbar/SidebarSubmenuAccordionContext';
import { DISPLAY_ENV_WARNING } from '@/constants/env';

import type { LayoutProps } from './types';

import SidebarLogoMark from '@/assets/logo/logo-m.svg';
import SidebarLogoMarkExpanded from '@/assets/logo/logo-m-expanded.svg';

import layoutClasses from './Layout.module.css';
import navBarClasses from './nav/navbar/Navbar.module.css';
import { useAuth } from '@/hooks/useAuth';
import { QUOINTELLIGENCE_CLIENT_ID } from '@/constants';
import { CLIENT_WARNING, ENV_WARNING } from '@/constants/text/english';

import { useKeyPressed } from '@/hooks/useKeyPressed';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { AppErrorBoundary } from '@/components/errors/AppErrorBoundary';
import ExperimentalFeaturesModal from '@/client/features/featureflags/components/ExperimentalFeaturesModal';

/** Desktop sidebar: Figma menu column 258px + navbar `p="md"` (~32px). */
const SIDEBAR_WIDTH_EXPANDED_PX = 292;
/** Icon rail: room for 20px icons + md padding without a cramped strip. */
const SIDEBAR_WIDTH_COLLAPSED_PX = 60;

const SIDEBAR_STORAGE_KEY = 'mercury-sidebar-expanded';

function readSidebarPreference(): boolean {
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (stored !== null) return stored === '1';
  } catch {
    /* private browsing / quota */
  }
  return false;
}

const Layout = ({
  routes,
  topbarItems,
  includeSearch,
  displayInitialModals,
  useClientTopbarActions,
}: LayoutProps) => {
  const [mobileOpened, setMobileOpened] = useState(false);
  const toggleMobile = useCallback(() => setMobileOpened((v) => !v), []);
  const closeMobile = useCallback(() => setMobileOpened(false), []);

  const [desktopOpened, setDesktopOpened] = useState(readSidebarPreference);
  const toggleDesktop = useCallback(() => {
    setDesktopOpened((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* noop */
      }
      return next;
    });
  }, []);
  const auth = useAuth();
  const { pathname } = useLocation();
  useScrollToTop(pathname);
  const [scroll] = useWindowScroll();
  const ref = useClickOutside(closeMobile);
  const isScrolled = scroll.y > 50;
  const hasClientWarning = auth.tokenInfo.client_access_id === QUOINTELLIGENCE_CLIENT_ID;
  const margin = [hasClientWarning, DISPLAY_ENV_WARNING].filter(Boolean);

  const [featuresOpen, setFeaturesOpen] = useState(false);
  const xPressed = useKeyPressed('x');

  const sideBarExpanded = mobileOpened || desktopOpened;
  // Both marks use `currentColor` for the letterforms, so they adapt to the
  // active theme via CSS (`color: var(--mantine-color-text)`).
  const SidebarLogo = sideBarExpanded ? SidebarLogoMarkExpanded : SidebarLogoMark;

  return (
    <>
      <AppShell
        layout="alt"
        header={{ height: margin.length > 0 ? margin.length * 35 + 60 : 60, offset: true }}
        navbar={{
          width: desktopOpened ? SIDEBAR_WIDTH_EXPANDED_PX : SIDEBAR_WIDTH_COLLAPSED_PX,
          breakpoint: 'sm',
          collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
        }}
        withBorder={false}
        transitionDuration={300}
        transitionTimingFunction="cubic-bezier(0.44, 0.8, 0.71, 0.98)"
        classNames={{
          root: layoutClasses.root,
          navbar: `${layoutClasses.navbar} ${layoutClasses.navbarMoon}`,
          header: `${layoutClasses.header} ${isScrolled ? layoutClasses.scrolled : ''}`,
        }}
      >
        <AppShell.Header>
          {DISPLAY_ENV_WARNING && <HeaderDisclaimer text={ENV_WARNING} />}
          {hasClientWarning && <HeaderDisclaimer text={CLIENT_WARNING} />}

          <Group h={60} px="md" justify="space-between">
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
            <TopBar
              topbarItems={topbarItems}
              scrolled={isScrolled}
              includeSearch={includeSearch}
              useClientTopbarActions={useClientTopbarActions}
            />
          </Group>
        </AppShell.Header>
        <AppShell.Navbar p="md" ref={ref} id="sidebar">
          <Group>
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
          </Group>

          <AppShell.Section className={layoutClasses.logoSection}>
            <Box
              className={layoutClasses.logoWrapper}
              onClick={() => {
                if (xPressed) {
                  setFeaturesOpen(true);
                }
              }}
              style={{ cursor: xPressed ? 'pointer' : 'default' }}
            >
              <SidebarLogo
                className={`${layoutClasses.sidebarLogoMark}${sideBarExpanded ? ` ${layoutClasses.sidebarLogoExpanded}` : ''}`}
                aria-hidden
              />
            </Box>
          </AppShell.Section>

          <AppShell.Section grow my="md" component={ScrollArea} scrollbars="y">
            <SidebarSubmenuAccordionProvider routes={routes}>
              {mapRoutesToMenu({ routes, sideBarExpanded })}
            </SidebarSubmenuAccordionProvider>
          </AppShell.Section>

          <AppShell.Section>
            <Box className={navBarClasses.sidebarFooterStack}>
              <Box visibleFrom="sm">
                <SidebarThemeToggle desktopOpened={desktopOpened} />
              </Box>
              <Documentation desktopOpened={desktopOpened} />
              <NewsLink desktopOpened={desktopOpened} />
              <Box visibleFrom="sm">
                <Expand toggleDesktop={toggleDesktop} desktopOpened={desktopOpened} />
              </Box>
            </Box>
          </AppShell.Section>
        </AppShell.Navbar>
        <AppShell.Main>
          <Suspense
            fallback={
              <Loading
                text="Loading assets"
                height="calc(100vh - calc(var(--app-shell-header-offset, 0rem) + var(--app-shell-padding)))"
              />
            }
          >
            <Box pt={15}>
              <AppErrorBoundary key={pathname}>
                <Outlet />
              </AppErrorBoundary>
            </Box>
          </Suspense>
        </AppShell.Main>
      </AppShell>

      {displayInitialModals && <InitialModals />}

      <ExperimentalFeaturesModal
        opened={featuresOpen}
        onClose={() => {
          setFeaturesOpen(false);
          window.location.reload();
        }}
      />
    </>
  );
};

export default Layout;
